import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProfile, getProfileStats } from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";
import { useAppConfig } from "../context/AppConfigContext";
import { ArrowLeft, Eye, MousePointer, Share2, Download, QrCode, Phone, Globe, MapPin, CreditCard, Info, ChevronDown } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const EVENT_TYPE_KEYS = [
  { value: "", tKey: "stats.allEvents", icon: Eye, color: "#6366f1" },
  { value: "view", tKey: "stats.views", icon: Eye, color: "#3b82f6" },
  { value: "contact_click", tKey: "stats.contactClicks", icon: Phone, color: "#10b981" },
  { value: "social_click", tKey: "stats.socialClicks", icon: Globe, color: "#8b5cf6" },
  { value: "link_click", tKey: "stats.linkClicks", icon: MousePointer, color: "#f59e0b" },
  { value: "share", tKey: "stats.shares", icon: Share2, color: "#ec4899" },
  { value: "vcard_download", tKey: "stats.vcardDownloads", icon: Download, color: "#14b8a6" },
  { value: "qr_scan", tKey: "stats.qrScans", icon: QrCode, color: "#f97316" },
];

const REFERRER_KEYS = {
  direct: { tKey: "stats.referrerDirect", color: "#94a3b8" },
  qr: { tKey: "stats.referrerQr", color: "#f97316" },
  nfc: { tKey: "stats.referrerNfc", color: "#6366f1" },
  whatsapp: { tKey: "stats.referrerWhatsapp", color: "#22c55e" },
  instagram: { tKey: "stats.referrerInstagram", color: "#e11d48" },
  facebook: { tKey: "stats.referrerFacebook", color: "#3b82f6" },
  email: { tKey: "stats.referrerEmail", color: "#14b8a6" },
  flyer: { tKey: "stats.referrerFlyer", color: "#a855f7" },
  share: { tKey: "stats.referrerShare", color: "#ec4899" },
  vcard: { tKey: "stats.referrerVcard", color: "#0ea5e9" },
  other: { tKey: "stats.referrerOther", color: "#78716c" },
};

const DAY_VALUES = [7, 14, 30, 90];

export default function ProfileStatsPage() {
  const { slug: id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginLogo } = useAppConfig();

  const EVENT_TYPES = EVENT_TYPE_KEYS.map((et) => ({ ...et, label: t(et.tKey) }));
  const uniqueLabel = t("stats.unique");
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [eventType, setEventType] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    getProfile(id)
      .then((res) => setProfile(res.data.profile))
      .catch(() => navigate("/app"));
  }, [id, navigate]);

  useEffect(() => {
    if (!profile) return;
    setLoading(true);
    getProfileStats(id, { days, type: eventType || undefined })
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, profile, days, eventType]);

  const selectedType = EVENT_TYPES.find((t) => t.value === eventType) || EVENT_TYPES[0];
  const totalEvents = stats ? Object.values(stats.totals).reduce((a, b) => a + b, 0) : 0;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <div id="profile-stats-page" className="max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/app/profile/${id}`)}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t("stats.title")}</h1>
          <p className="text-sm text-gray-500">{profile?.name || ""}</p>
        </div>
      </div>

      {/* How stats work */}
      <div className="mb-6">
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition"
        >
          <Info className="w-4 h-4" />
          {t("stats.howItWorks")}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showInfo ? "rotate-180" : ""}`} />
        </button>
        {showInfo && (
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
            <p className="text-sm text-blue-800">{t("stats.rule1")}</p>
            <p className="text-sm text-blue-800">{t("stats.rule2")}</p>
            <p className="text-sm text-blue-800">{t("stats.rule3")}</p>
            <p className="text-sm text-blue-800">{t("stats.rule4")}</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div id="profile-stats-filters" className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {DAY_VALUES.map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-4 py-2 text-sm font-medium transition ${
                days === n
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t("stats.days").replace("{n}", n)}
            </button>
          ))}
        </div>
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          className="rounded-lg border border-gray-200 px-4 py-2 text-sm bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
        >
          {EVENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div id="profile-stats-totals" className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {EVENT_TYPES.filter((t) => t.value).map((t) => {
            const Icon = t.icon;
            const count = stats.totals[t.value] || 0;
            const unique = stats.unique?.[t.value] || 0;
            return (
              <button
                key={t.value}
                onClick={() => setEventType(eventType === t.value ? "" : t.value)}
                className={`bg-white rounded-xl border p-4 text-left transition hover:shadow-sm ${
                  eventType === t.value ? "border-indigo-400 ring-2 ring-indigo-100" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg" style={{ backgroundColor: t.color + "15" }}>
                    <Icon className="h-4 w-4" style={{ color: t.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                {unique > 0 && (
                  <p className="text-xs text-indigo-500 font-medium">{unique} {uniqueLabel}</p>
                )}
                <p className="text-xs text-gray-500">{t.label}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Card Taps */}
      {stats && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-8 flex items-center gap-4">
          {loginLogo ? (
            <img src={loginLogo} alt="" className="h-10 w-10 object-contain" />
          ) : (
            <div className="p-2.5 rounded-lg" style={{ backgroundColor: "#8b5cf615" }}>
              <CreditCard className="h-5 w-5" style={{ color: "#8b5cf6" }} />
            </div>
          )}
          <div>
            <p className="text-xl font-bold text-gray-900">{stats.card_scans}</p>
            <p className="text-xs text-gray-500">{t("stats.cardTaps")}</p>
          </div>
          {stats.card_unique_scans > 0 && (
            <div className="ml-4 pl-4 border-l border-gray-200">
              <p className="text-xl font-bold text-gray-900">{stats.card_unique_scans}</p>
              <p className="text-xs text-gray-500">{uniqueLabel}</p>
            </div>
          )}
        </div>
      )}

      {/* Chart */}
      <div id="profile-stats-chart" className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">
            {selectedType.label}
            <span className="text-gray-400 font-normal ml-2">{t("stats.last").replace("{n}", days)}</span>
          </h2>
          <span className="text-sm text-gray-400">{totalEvents} {t("stats.total")}</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent" />
          </div>
        ) : stats?.chart?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12, fill: "#94a3b8" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: "#94a3b8" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <Tooltip
                labelFormatter={formatDate}
                contentStyle={{
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  fontSize: "13px",
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={selectedType.color}
                strokeWidth={2.5}
                dot={{ r: 4, fill: selectedType.color, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: selectedType.color, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            {t("stats.noActivity")}
          </div>
        )}
      </div>

      {/* Traffic Sources */}
      {stats?.referrers && Object.keys(stats.referrers).length > 0 && (() => {
        const entries = Object.entries(stats.referrers).sort((a, b) => b[1] - a[1]);
        const maxCount = entries[0]?.[1] || 1;
        return (
          <div id="profile-stats-referrers" className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-gray-400" />
              <h2 className="font-semibold text-gray-800">{t("stats.referrers")}</h2>
            </div>
            <div className="space-y-3 mb-6">
              {entries.map(([ref, count]) => {
                const info = REFERRER_KEYS[ref] || REFERRER_KEYS.other;
                const label = t(info.tKey);
                const pct = Math.round((count / maxCount) * 100);
                return (
                  <div key={ref}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      <span className="text-sm text-gray-500">{count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: info.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Referrer line chart */}
            {stats.referrer_chart?.length > 1 && (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={stats.referrer_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                  />
                  <Tooltip
                    labelFormatter={formatDate}
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                      fontSize: "13px",
                    }}
                  />
                  <Legend
                    formatter={(value) => {
                      const info = REFERRER_KEYS[value];
                      return info ? t(info.tKey) : value;
                    }}
                  />
                  {entries.map(([ref]) => {
                    const info = REFERRER_KEYS[ref] || REFERRER_KEYS.other;
                    return (
                      <Line
                        key={ref}
                        type="monotone"
                        dataKey={ref}
                        name={ref}
                        stroke={info.color}
                        strokeWidth={2}
                        dot={{ r: 3, fill: info.color, strokeWidth: 0 }}
                        activeDot={{ r: 5, fill: info.color, strokeWidth: 0 }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        );
      })()}

      {/* Event Log */}
      <div id="profile-stats-event-log" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">{t("stats.recentEvents")}</h2>
        </div>
        {stats?.events?.length > 0 ? (
          <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
            {stats.events.map((event) => {
              const typeInfo = EVENT_TYPES.find((t) => t.value === event.event_type) || EVENT_TYPES[0];
              const Icon = typeInfo.icon;
              const ref = event.event_data?.ref;
              const refLabel = ref ? (REFERRER_KEYS[ref] ? t(REFERRER_KEYS[ref].tKey) : ref) : "";
              const detail = event.event_data?.platform || event.event_data?.type || event.event_data?.label || refLabel || "";
              return (
                <div key={event.id} className="px-6 py-3 flex items-center gap-4 hover:bg-gray-50 transition">
                  <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: typeInfo.color + "15" }}>
                    <Icon className="h-4 w-4" style={{ color: typeInfo.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{typeInfo.label}</p>
                    {detail && <p className="text-xs text-gray-400 truncate">{detail}</p>}
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {new Date(event.created_at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="px-6 py-8 text-center text-sm text-gray-400">{t("stats.noEvents")}</p>
        )}
      </div>
    </div>
  );
}
